import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/additionalInfoStyle';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import CustomInput from 'components/CustomInput/CustomInput';
import { translate } from 'components/Translation';
import { AccountsService } from 'services/accounts/accounts';
import AlternativeSelect, { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import AddressesService from 'services/addresses/addresses';
import { getLanguage } from 'utils/language/language';
import { setToast } from 'redux/modules/toast/actions';
import { useDispatch } from 'react-redux';
import invalidateCache from 'utils/api/invalidateCache';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { BodyS } from 'components/Typography/Typography';
import { AccountDetails as AccountDetailsForm } from 'utils/api/payloads/omantelApi';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IAdditionalInfoDialogProps {
	isOpen: boolean;
	onClose: () => void;
	formData: { [index: string]: never | string };
	setFormCardData: (key: string, value: string) => void;
	addressSuccessfullyUpdated: () => void;
}

type Select = 'GOVERNORATE' | 'REGION' | 'CITY';

type Errors = {
	way?: string | null;
	region?: string | null;
	city?: string | null;
};

const AdditionalInfoDialog = ({
	isOpen,
	onClose,
	formData,
	setFormCardData,
	addressSuccessfullyUpdated,
}: IAdditionalInfoDialogProps) => {
	const [governorates, setGovertnorates] = useState<Array<string>>([]);
	const [regions, setRegions] = useState([]);
	const [cities, setCities] = useState([]);
	const [selectedGovernorate, setSelectedGovernorate] = useState<string | null>(null);
	const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
	const [selectedCity, setSelectedCity] = useState<string | null>(null);
	const [errors, setErrors] = useState<Errors>({
		way: null,
		region: null,
		city: null,
	});
	const classes = useStyles();
	const dispatch = useDispatch();
	const isMounted = useRef(true);

	const getRegionByGovernorate = useCallback(() => {
		if (selectedGovernorate && regions.length) {
			return regions
				.filter((region: any) => region.region === selectedGovernorate)
				.map((filteredRegion: any) => filteredRegion.key);
		}
		return [];
	}, [selectedGovernorate, regions]);

	const getCitiesByRegion = useCallback(() => {
		if (selectedRegion && cities.length) {
			const citiesByRegion: any = cities.find((city: { key: string }) => city.key === selectedRegion);
			if (citiesByRegion) {
				return citiesByRegion.cities.map((city: any) => {
					const lang = getLanguage() === ('en' as types.redux.settings.language) ? 'name_en' : 'name_ar';
					return {
						id: city.key,
						name: city[lang],
					};
				});
			} else {
				return [{ id: selectedRegion, name: selectedRegion }];
			}
		}
		return [];
	}, [selectedRegion, cities]);

	const handleClose = () => {
		onClose();
	};

	const validate = (accountDetails: { [index: string]: never | string }) => {
		let validityErrors: Errors = {};
		if (!accountDetails.way) {
			validityErrors.way = translate('field.required');
		}
		if (!selectedRegion) {
			validityErrors.region = translate('field.required');
		}
		if (!selectedCity) {
			validityErrors.city = translate('field.required');
		}
		if (Object.keys(validityErrors).length > 0) {
			setErrors((errors) => {
				return { ...errors, ...validityErrors };
			});
			return false;
		}
		return true;
	};

	const trimData = (data: { [index: string]: never | string }) => {
		return Object.keys(data).reduce(
			(acc, key) => ({ ...acc, [key]: typeof data[key] === 'string' ? data[key].trim() : data[key] }),
			{}
		);
	};

	const handleSubmit = (accountDetails: { [index: string]: never | string }) => {
		if (validate(accountDetails)) {
			dispatch(showLoadingIndicator());
			AccountsService.updateCorpAddress(
				trimData((accountDetails as unknown) as { [index: string]: never | string }) as AccountDetailsForm
			).subscribe(
				() => {
					dispatch(
						setToast({
							error: false,
							actionType: translate('edited'),
							label: translate('organizations.additional-info.success'),
						})
					);
					dispatch(hideLoadingIndicator());
					invalidateCache(['getCorpAccountDetails']);
					addressSuccessfullyUpdated();
				},
				() => {
					dispatch(hideLoadingIndicator());
					dispatch(
						setToast({
							error: true,
							actionType: translate('edited'),
							label: translate('error.generic-title'),
						})
					);
				}
			);
		}
	};

	const handleSelect = (item: string, type: Select) => {
		switch (type) {
			case 'GOVERNORATE': {
				setSelectedGovernorate(item);
				setSelectedRegion(null);
				setSelectedCity(null);
				setFormCardData('governorate', item);
				setFormCardData('region', '');
				setFormCardData('city', '');
				break;
			}
			case 'REGION': {
				setSelectedRegion(item);
				setSelectedCity(null);
				setFormCardData('region', item);
				setFormCardData('city', '');
				break;
			}
			case 'CITY': {
				setSelectedCity(item);
				setFormCardData('city', item);
				break;
			}
			default: {
				break;
			}
		}
	};

	useEffect(() => {
		AddressesService.getRegions().subscribe(({ response }) => {
			if (isMounted.current) {
				setRegions(response);
				setGovertnorates([...new Set<string>(response.map((region: any) => region.region))]);

				if (formData.region) {
					const initialRegion = response.find((region: any) => region.key === formData.region);
					setSelectedGovernorate(initialRegion.region);
					setSelectedRegion(formData.region);
				}
				if (formData.city) {
					setSelectedCity(formData.city);
				}
			}
		});
	}, [formData.city, formData.region]);

	useEffect(() => {
		AddressesService.getCities().subscribe(({ response }) => {
			if (isMounted.current) {
				setCities(response);
			}
		});
	}, []);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={handleClose}
			header={translate('organizations.additional-info.title')}
			rightSideButtons={[
				{
					text: translate('cancel'),
					onClick: handleClose,
					color: 'tertiary-light',
				},
				{
					text: translate('save'),
					onClick: () => {
						handleSubmit(formData);
					},
					color: 'primary-light',
				},
			]}
			dialogBody={
				<div className={classes.formContainer}>
					<div style={{ position: 'relative' }}>
						<AlternativeSelect
							label={translate('governorate')}
							value={
								<div className={classes.selectedItem}>
									<BodyS color="primary-light">
										<>{selectedGovernorate ? selectedGovernorate : translate('governorate')}</>
									</BodyS>
								</div>
							}
							id="governorate-select"
							onClick={(item: string) => handleSelect(item, 'GOVERNORATE')}
							items={governorates}
							selectedItems={selectedGovernorate ? [selectedGovernorate] : []}
						/>
						<AlternativeSelect
							label={translate('region')}
							value={
								<div className={classes.selectedItem}>
									<BodyS color="primary-light">
										{selectedRegion ? selectedRegion : translate('region')}
									</BodyS>
								</div>
							}
							disabled={!selectedGovernorate}
							id="region-select"
							onClick={(item: string) => handleSelect(item, 'REGION')}
							error={errors.region}
							items={getRegionByGovernorate()}
							selectedItems={selectedRegion ? [selectedRegion] : []}
						/>
						<AlternativeSelect
							label={translate('city')}
							value={
								<div className={classes.selectedItem}>
									<BodyS color="primary-light">
										{selectedCity ? selectedCity : translate('city')}
									</BodyS>
								</div>
							}
							id="city-select"
							error={errors.city}
							disabled={!selectedGovernorate || !selectedRegion}
							onClick={(item: IAlternativeSelectItem) => handleSelect(item.name, 'CITY')}
							items={getCitiesByRegion()}
							selectedItems={selectedCity ? [selectedCity] : []}
						/>
						<CustomInput
							id="way-number-input"
							labelText={translate('way.number')}
							formControlProps={{ fullWidth: true }}
							error={!!errors.way}
							helperText={!!errors.way && <span className={classes.error}>{errors.way}</span>}
							inputProps={{
								value: formData.way,
								onChange: ({ target: { value } }: { target: HTMLInputElement }) =>
									setFormCardData('way', value),
							}}
						/>
						<div className={classes.monthCvvContainer}>
							<div>
								<CustomInput
									id="house-number-input"
									labelText={translate('house.number')}
									formControlProps={{ fullWidth: true }}
									inputProps={{
										value: formData.house,
										onChange: ({ target: { value } }: { target: HTMLInputElement }) =>
											setFormCardData('house', value),
									}}
								/>
							</div>
							<div>
								<CustomInput
									id="apartment-number-input"
									labelText={translate('apartment.number')}
									formControlProps={{ fullWidth: true }}
									inputProps={{
										value: formData.apartment,
										onChange: ({ target: { value } }: { target: HTMLInputElement }) =>
											setFormCardData('apartment', value),
									}}
								/>
							</div>
						</div>
						<CustomInput
							id="landmark-input"
							labelText={translate('landmark')}
							formControlProps={{ fullWidth: true }}
							inputProps={{
								value: formData.landmark,
								onChange: ({ target: { value } }: { target: HTMLInputElement }) =>
									setFormCardData('landmark', value),
							}}
						/>
					</div>
				</div>
			}
		/>
	);
};

export default AdditionalInfoDialog;
